import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    BookOpen,
    Gift,
    ListChecks,
    Calculator,
    Layers,
    Target,
    ChevronRight,
    Search,
} from "lucide-react";
import { GUIDES, ARTICLES, ARTICLE_CATEGORIES } from "../lib/data";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, breadcrumbSchema } from "../components/SEO";

const ICON_MAP = {
    BookOpen,
    Gift,
    ListChecks,
    Calculator,
    Layers,
    Target,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Guides = () => {
    const [cat, setCat] = useState("Tous");
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [articles, setArticles] = useState(ARTICLES);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        let active = true;
        axios
            .get(`${API}/articles`)
            .then(({ data }) => {
                if (active) {
                    setArticles(data);
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch articles:", err);
                if (active) {
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, []);

    React.useEffect(() => {
        setPage(1);
    }, [cat, q]);

    const items = useMemo(() => {
        return articles.filter((a) => {
            const matchCat = cat === "Tous" || a.category === cat;
            const matchQ =
                !q ||
                a.title.toLowerCase().includes(q.toLowerCase()) ||
                a.excerpt.toLowerCase().includes(q.toLowerCase());
            return matchCat && matchQ;
        });
    }, [articles, cat, q]);

    const ITEMS_PER_PAGE = 6;
    const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
    const displayedItems = useMemo(() => {
        return items.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
    }, [items, page]);

    return (
        <div data-testid="guides-page">
            <SEO
                title="Analyses, Pronostics et Guides Paris Football | Pronospot.com"
                description="Optimisez vos pronostics avec nos analyses de matchs, notre suivi des bonus, et nos guides complets pour parier sur le football comme un professionnel."
                jsonLd={breadcrumbSchema([
                    { name: "Accueil", url: "https://pronospot.com/" },
                    { name: "Analyses & Guides", url: "https://pronospot.com/guides" },
                ])}
            />
            <section className="container-x pt-10 pb-6">
                <Breadcrumbs items={[{ label: "Analyses & Guides" }]} />
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">
                    Analyses & Guides de paris football
                </h1>
                <p className="text-gray-400 mt-3 max-w-2xl">
                    Optimisez vos pronostics avec nos analyses de matchs, notre suivi des bonus, et nos guides complets pour parier sur le football comme un professionnel.
                </p>
            </section>

            {/* Static Guides Section */}
            <section className="container-x pb-10">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight mb-6">
                    Guides pratiques essentiels
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {GUIDES.map((g) => {
                        const Icon = ICON_MAP[g.icon] || BookOpen;
                        return (
                            <Link
                                key={g.slug}
                                to={`/guides/${g.slug}`}
                                className="card-elev p-6 flex flex-col group"
                                data-testid={`guide-card-${g.slug}`}
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#00C853]/10 border border-[#00C853]/30 flex items-center justify-center mb-4 group-hover:bg-[#00C853]/20">
                                    <Icon
                                        className="text-[#00C853]"
                                        size={22}
                                    />
                                </div>
                                <span className="chip self-start mb-3">
                                    {g.category}
                                </span>
                                <h3 className="font-heading font-bold text-white text-lg leading-snug mb-3">
                                    {g.title}
                                </h3>
                                <p className="text-sm text-gray-400 flex-1 leading-relaxed">
                                    {g.excerpt}
                                </p>
                                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#2A4A6B]">
                                    <span className="text-xs text-gray-500">
                                        {g.readTime} de lecture
                                    </span>
                                    <span className="text-[#00C853] text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                        Lire le guide
                                        <ChevronRight size={14} />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            <div className="container-x">
                <div className="border-t border-[#2A4A6B] my-8" />
            </div>

            {/* Dynamic Analyses & Articles Section */}
            <section className="container-x pb-20">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight mb-6">
                    Toutes nos analyses & guides
                </h2>

                {/* Filters */}
                <div className="card-elev p-5 flex flex-col md:flex-row gap-4 items-stretch md:items-center mb-8">
                    <div className="relative flex-1">
                        <Search
                            size={18}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher une analyse, un guide ou un pronostic..."
                            data-testid="search-articles-input"
                            className="w-full bg-white dark:bg-[#0D1B2A] border border-slate-300 dark:border-[#2A4A6B] rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {ARTICLE_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCat(c)}
                                data-testid={`filter-cat-${c.toLowerCase()}`}
                                className={`text-sm px-4 py-2 rounded-full border transition-all ${
                                    cat === c
                                        ? "bg-[#00C853] text-black border-[#00C853] font-semibold"
                                        : "bg-transparent text-slate-600 dark:text-gray-300 border-slate-300 dark:border-[#2A4A6B] hover:border-[#00C853]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Articles Grid */}
                {items.length === 0 ? (
                    <div className="text-center py-16 text-gray-400 card-elev">
                        Aucun article ne correspond à votre recherche.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayedItems.map((a) => {
                            const articleLink = a.link || `/articles/${a.slug}`;
                            return (
                                <Link
                                    key={a.slug}
                                    to={articleLink}
                                    className="card-elev p-6 flex flex-col hover:border-[#00C853] transition-all cursor-pointer"
                                    data-testid={`actu-card-${a.slug}`}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="chip">{a.category}</span>
                                    </div>
                                    <h3 className="font-heading font-bold text-white text-lg leading-snug mb-3">
                                        {a.title}
                                    </h3>
                                    <p className="text-sm text-gray-400 flex-1 leading-relaxed">
                                        {a.excerpt}
                                    </p>
                                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#2A4A6B]">
                                        <span className="text-xs text-gray-500">
                                            {a.date} · {a.author}
                                        </span>
                                        <span className="text-[#00C853] text-sm font-semibold inline-flex items-center gap-1">
                                            Lire
                                            <ChevronRight size={14} />
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-12" data-testid="articles-pagination">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className="btn-outline !py-2 !px-3 text-xs"
                            disabled={page === 1}
                            data-testid="pagination-prev"
                        >
                            Précédent
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPage(p)}
                                data-testid={`pagination-page-${p}`}
                                className={`px-4 py-2 rounded-md text-xs font-bold transition-all ${
                                    page === p
                                        ? "bg-[#00C853] text-black font-semibold"
                                        : "btn-outline hover:border-[#00C853]"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            className="btn-outline !py-2 !px-3 text-xs"
                            disabled={page === totalPages}
                            data-testid="pagination-next"
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </section>
        </div>
    );
};

export default Guides;
